import {
  EditOutlined,
  HolderOutlined,
  InboxOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  RightOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import { Button, Dropdown, Input, Modal, Select, Spin, Tooltip } from 'antd'
import type { MenuProps } from 'antd'
import { FaqArticleReorderPayload, FaqArticleSummary, FaqCategoryItem } from 'Modules'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { generatePath, Link, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import ProtectedButton from 'component/button/protected-button'
import {
  FAQ_ARTICLE_STATUS_OPTIONS,
  FAQ_CATEGORY_VISIBILITY_OPTIONS,
  MAX_FAQ_ARTICLES_PER_CATEGORY,
  MAX_FAQ_CATEGORIES,
} from 'constants/faq'
import { FEATURE } from 'constants/feature'
import {
  applyArticleCrossCategoryMove,
  applyArticleReorderInCategory,
  applyCategoryReorder,
} from 'hooks/faq/faq-list-optimistic'
import {
  useDeleteFaqArticle,
  useDeleteFaqCategory,
  useFaqCategoryList,
  useReorderFaqArticle,
  useReorderFaqCategory,
  useUpdateFaqArticleStatus,
  useUpdateFaqCategory,
} from 'hooks/faq'
import { usePermission } from 'hooks/use-permission'
import { PAGES } from 'routes'
import { getApiErrorCode, getApiErrorMessage } from 'utils/api-error'
import { formatDateWithPattern } from 'utils/format-time'

import style from './faq-management-panel.module.scss'

interface FaqManagementPanelProps {
  onEditCategory: (item: FaqCategoryItem) => void
  onCreateCategory: () => void
  /** Refetch current list (after mutate). */
  onRefresh: () => void
  /** Clear search/filters and reload the full list. */
  onReloadFilters: () => void
  search: string
  onSearchChange: (value: string) => void
  categoryVisibility: string
  onCategoryVisibilityChange: (value: string) => void
  articleStatus: string
  onArticleStatusChange: (value: string) => void
  expandCategoryId?: string | null
  onExpandCategoryHandled?: () => void
}

const getFilteredEmptyCopy = (
  total: number,
  search: string,
  articleStatus: string,
  categoryVisibility: string,
): { title: string; description?: string } => {
  if (total === 0) {
    return {
      title: 'No categories yet',
      description: 'Create a category to start adding FAQ articles.',
    }
  }
  if (search.trim()) {
    return { title: 'No articles match your search' }
  }
  if (articleStatus) {
    return { title: 'No articles match your filters' }
  }
  if (categoryVisibility) {
    return { title: 'No categories match your filters' }
  }
  return { title: 'No categories match your filters' }
}

type DraggedArticle = { id: string; sourceCategoryId: string }

type PendingArticleMove = {
  articleId: string
  sourceCategoryId: string
  targetCategoryId: string
  dropIndex: number
  payload: FaqArticleReorderPayload
  targetCategoryName: string
}

const buildListReorderNeighbors = <T extends { _id: string }>(
  items: T[],
  dragIndex: number,
  dropIndex: number,
): { afterId: string | null; beforeId: string | null } | null => {
  if (dragIndex === dropIndex) {
    return null
  }
  const next = [...items]
  const [removed] = next.splice(dragIndex, 1)
  next.splice(dropIndex, 0, removed)
  const newIndex = dropIndex
  return {
    afterId: newIndex > 0 ? next[newIndex - 1]._id : null,
    beforeId: newIndex < next.length - 1 ? next[newIndex + 1]._id : null,
  }
}

const buildArticleDropNeighbors = (
  targetArticles: FaqArticleSummary[],
  articleId: string,
  dropIndex: number,
) => {
  const without = targetArticles.filter((article) => article._id !== articleId)
  const next = [...without]
  next.splice(dropIndex, 0, { _id: articleId } as FaqArticleSummary)
  return {
    afterId: dropIndex > 0 ? next[dropIndex - 1]._id : null,
    beforeId: dropIndex < next.length - 1 ? next[dropIndex + 1]._id : null,
  }
}

export const FaqManagementPanel: React.FC<FaqManagementPanelProps> = ({
  onEditCategory,
  onCreateCategory,
  onRefresh,
  onReloadFilters,
  search,
  onSearchChange,
  categoryVisibility,
  onCategoryVisibilityChange,
  articleStatus,
  onArticleStatusChange,
  expandCategoryId,
  onExpandCategoryHandled,
}) => {
  const listParams = useMemo(
    () => ({ search, articleStatus, categoryVisibility }),
    [search, articleStatus, categoryVisibility],
  )
  const navigate = useNavigate()
  const { items, total, maxCategories, isLoading, isFetching } = useFaqCategoryList(listParams)
  // Create Article availability ignores search/filters
  const { items: allCategories, isLoading: allCategoriesLoading } = useFaqCategoryList()
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set())
  const [draggedCategoryId, setDraggedCategoryId] = useState<string | null>(null)
  const [draggedArticle, setDraggedArticle] = useState<DraggedArticle | null>(null)
  const [dragOverCategoryIndex, setDragOverCategoryIndex] = useState<number | null>(null)
  const [dragOverArticleKey, setDragOverArticleKey] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<FaqCategoryItem | null>(null)
  const [cannotDeleteTarget, setCannotDeleteTarget] = useState<FaqCategoryItem | null>(null)
  const [visibilityTarget, setVisibilityTarget] = useState<FaqCategoryItem | null>(null)
  const [deleteArticleTarget, setDeleteArticleTarget] = useState<FaqArticleSummary | null>(null)
  const [statusTarget, setStatusTarget] = useState<FaqArticleSummary | null>(null)
  const [pendingMove, setPendingMove] = useState<PendingArticleMove | null>(null)

  const deleteCategory = useDeleteFaqCategory()
  const deleteArticle = useDeleteFaqArticle()
  const updateArticleStatus = useUpdateFaqArticleStatus()
  const updateCategory = useUpdateFaqCategory()
  const reorderCategory = useReorderFaqCategory(listParams)
  const reorderArticle = useReorderFaqArticle(listParams)

  // View-only roles (e.g. Admin) can browse and open articles but get no write actions
  const { canCRUD } = usePermission()
  const canEdit = canCRUD(FEATURE.FAQ_CREATE)
  const filtersActive = Boolean(search.trim() || articleStatus || categoryVisibility)
  const dragDisabled = !canEdit || filtersActive
  const filterLocksExpand = Boolean(search.trim() || articleStatus)
  const didApplyDefaultExpand = useRef(false)

  useEffect(() => {
    if (items.length === 0) {
      return
    }
    if (filterLocksExpand) {
      setExpandedIds(new Set(items.map((c) => c._id)))
      return
    }
    if (!didApplyDefaultExpand.current) {
      didApplyDefaultExpand.current = true
      // a category requested via `expandCategoryId` replaces the default first one
      const defaultId = expandCategoryId ?? items[0]._id
      setExpandedIds((prev) => (prev.size > 0 ? prev : new Set([defaultId])))
    }
  }, [items, filterLocksExpand, expandCategoryId])

  useEffect(() => {
    if (!expandCategoryId) {
      return
    }
    setExpandedIds((prev) => new Set(prev).add(expandCategoryId))
    onExpandCategoryHandled?.()
  }, [expandCategoryId, onExpandCategoryHandled])

  const toggleExpanded = (id: string) => {
    if (filterLocksExpand) {
      return
    }
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const clearDragState = () => {
    setDraggedCategoryId(null)
    setDraggedArticle(null)
    setDragOverCategoryIndex(null)
    setDragOverArticleKey(null)
  }

  const runArticleReorder = async (
    articleId: string,
    payload: FaqArticleReorderPayload,
    nextResults?: FaqCategoryItem[],
  ) => {
    try {
      const result = await reorderArticle.mutateAsync({ id: articleId, payload, nextResults })
      if (result?.movedToCategory) {
        toast.success(`Article moved to ${result.movedToCategory.name}`)
        setExpandedIds((prev) => new Set(prev).add(result.movedToCategory!._id))
      } else {
        toast.success('Order updated')
      }
    } catch (error: unknown) {
      const code = getApiErrorCode(error)
      if (code === 'FAQ_LIST_CHANGED') {
        toast.error('The list has changed. Please refresh and try again.')
      } else if (code === 'FAQ_ARTICLE_CATEGORY_FULL') {
        toast.error('This category already has 20 articles')
      } else {
        toast.error(getApiErrorMessage(error, 'Failed to reorder article'))
      }
    }
  }

  const handleVisibilityChange = async () => {
    if (!visibilityTarget) {
      return
    }
    const nextVisibility = visibilityTarget.visibility === 'VISIBLE' ? 'HIDDEN' : 'VISIBLE'
    try {
      await updateCategory.mutateAsync({
        id: visibilityTarget._id,
        payload: { name: visibilityTarget.name, visibility: nextVisibility },
      })
      toast.success('Save changes successfully')
      setVisibilityTarget(null)
      onRefresh()
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to update category'))
    }
  }

  const handleDeleteArticle = async (id: string) => {
    try {
      await deleteArticle.mutateAsync(id)
      toast.success('Delete article successfully')
      setDeleteArticleTarget(null)
      onRefresh()
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, 'Failed to delete article'))
    }
  }

  const handleToggleArticleStatus = async () => {
    if (!statusTarget) {
      return
    }
    const publishing = statusTarget.status !== 'PUBLISHED'
    try {
      await updateArticleStatus.mutateAsync({
        id: statusTarget._id,
        status: publishing ? 'PUBLISHED' : 'UNPUBLISHED',
      })
      toast.success(publishing ? 'Publish article successfully' : 'Unpublish article successfully')
      setStatusTarget(null)
      onRefresh()
    } catch (error: unknown) {
      toast.error(
        getApiErrorMessage(
          error,
          publishing ? 'Failed to publish article' : 'Failed to unpublish article'
        )
      )
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteCategory.mutateAsync(id)
      toast.success('Delete category successfully')
      setDeleteTarget(null)
      onRefresh()
    } catch (error: unknown) {
      const code = getApiErrorCode(error)
      if (code === 'FAQ_CATEGORY_NOT_EMPTY') {
        toast.error('Cannot delete category while it still has articles')
      } else {
        toast.error(getApiErrorMessage(error, 'Failed to delete category'))
      }
    }
  }

  const handleCategoryDrop = async (filteredIndex: number) => {
    if (!draggedCategoryId) {
      return
    }
    const dropCategory = items[filteredIndex]
    if (!dropCategory) {
      return
    }
    const dragIndex = items.findIndex((item) => item._id === draggedCategoryId)
    const dropIndex = items.findIndex((item) => item._id === dropCategory._id)
    const neighbors = buildListReorderNeighbors(items, dragIndex, dropIndex)
    const movingCategoryId = draggedCategoryId
    clearDragState()
    if (!neighbors || dragIndex === -1 || !movingCategoryId) {
      return
    }
    const nextResults = applyCategoryReorder(items, movingCategoryId, dropIndex)
    try {
      await reorderCategory.mutateAsync({
        id: movingCategoryId,
        payload: { afterId: neighbors.afterId, beforeId: neighbors.beforeId },
        nextResults,
      })
      toast.success('Order updated')
    } catch (error: unknown) {
      const code = getApiErrorCode(error)
      if (code === 'FAQ_LIST_CHANGED') {
        toast.error('The list has changed. Please refresh and try again.')
      } else {
        toast.error(getApiErrorMessage(error, 'Failed to reorder'))
      }
    }
  }

  const handleArticleDrop = (
    targetCategory: FaqCategoryItem,
    dropIndex: number,
    targetArticles: FaqArticleSummary[],
  ) => {
    if (!draggedArticle || dragDisabled) {
      return
    }
    const { id: articleId, sourceCategoryId } = draggedArticle
    const neighbors = buildArticleDropNeighbors(targetArticles, articleId, dropIndex)
    const payload: FaqArticleReorderPayload = {
      afterId: neighbors.afterId,
      beforeId: neighbors.beforeId,
    }
    const isCrossCategory = sourceCategoryId !== targetCategory._id

    clearDragState()

    if (isCrossCategory) {
      if (targetCategory.articleCount >= MAX_FAQ_ARTICLES_PER_CATEGORY) {
        toast.error('This category already has 20 articles')
        return
      }
      setPendingMove({
        articleId,
        sourceCategoryId,
        targetCategoryId: targetCategory._id,
        dropIndex,
        payload: { ...payload, categoryId: targetCategory._id },
        targetCategoryName: targetCategory.name,
      })
      return
    }

    const nextResults = applyArticleReorderInCategory(
      items,
      targetCategory._id,
      articleId,
      dropIndex,
    )
    void runArticleReorder(articleId, payload, nextResults)
  }

  const confirmPendingMove = async () => {
    if (!pendingMove) {
      return
    }
    const { articleId, payload, sourceCategoryId, targetCategoryId, dropIndex } = pendingMove
    const nextResults = applyArticleCrossCategoryMove(
      items,
      articleId,
      sourceCategoryId,
      targetCategoryId,
      dropIndex,
    )
    setPendingMove(null)
    await runArticleReorder(articleId, payload, nextResults)
  }

  const handleMoveArticleVertical = async (
    category: FaqCategoryItem,
    article: FaqArticleSummary,
    position: 'top' | 'bottom',
  ) => {
    const articles = category.articles ?? []
    const index = articles.findIndex((row) => row._id === article._id)
    if (index === -1) {
      return
    }
    const targetIndex = position === 'top' ? 0 : articles.length - 1
    const neighbors = buildListReorderNeighbors(articles, index, targetIndex)
    if (!neighbors) {
      return
    }
    const nextResults = applyArticleReorderInCategory(items, category._id, article._id, targetIndex)
    await runArticleReorder(article._id, neighbors, nextResults)
  }

  const articleMenu = (
    category: FaqCategoryItem,
    article: FaqArticleSummary,
    index: number,
  ): MenuProps['items'] => {
    const articles = category.articles ?? []
    const atTop = index === 0
    const atBottom = index === articles.length - 1
    const onlyOne = articles.length <= 1
    return [
      {
        key: 'edit',
        label: 'Edit',
        onClick: () => navigate(generatePath(PAGES.FAQ_ARTICLE_EDIT, { id: article._id })),
      },
      {
        key: 'status',
        label: article.status === 'PUBLISHED' ? 'Unpublish' : 'Publish',
        onClick: () => setStatusTarget(article),
      },
      {
        key: 'move-top',
        label: 'Move to top',
        disabled: atTop || onlyOne,
        onClick: () => handleMoveArticleVertical(category, article, 'top'),
      },
      {
        key: 'move-bottom',
        label: 'Move to bottom',
        disabled: atBottom || onlyOne,
        onClick: () => handleMoveArticleVertical(category, article, 'bottom'),
      },
      { type: 'divider' },
      {
        key: 'delete',
        label: 'Delete',
        danger: true,
        onClick: () => setDeleteArticleTarget(article),
      },
    ]
  }

  const moreMenu = (category: FaqCategoryItem): MenuProps['items'] => [
    {
      key: 'visibility',
      label: category.visibility === 'VISIBLE' ? 'Hide category' : 'Show category',
      onClick: () => setVisibilityTarget(category),
    },
    {
      key: 'delete',
      label: 'Delete category',
      danger: true,
      onClick: () => {
        if (category.articleCount > 0) {
          setCannotDeleteTarget(category)
        } else {
          setDeleteTarget(category)
        }
      },
    },
  ]

  const emptyCopy = getFilteredEmptyCopy(total, search, articleStatus, categoryVisibility)

  const createArticleBlockedReason =
    allCategories.length === 0
      ? 'Create a category first'
      : allCategories.every((c) => c.articleCount >= MAX_FAQ_ARTICLES_PER_CATEGORY)
        ? 'All categories are full'
        : ''

  return (
    <div className={style.panel}>
      <div className={style.toolbar}>
        <div className={style.filters}>
          <Input
            allowClear
            placeholder="Search article"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            className={style.searchInput}
          />
          <Select
            allowClear
            placeholder="Article status"
            value={articleStatus || undefined}
            options={FAQ_ARTICLE_STATUS_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
            onChange={(value) => onArticleStatusChange(value ?? '')}
            className={style.filterSelect}
          />
          <Select
            allowClear
            placeholder="Category visibility"
            value={categoryVisibility || undefined}
            options={FAQ_CATEGORY_VISIBILITY_OPTIONS.map((o) => ({
              label: o.label,
              value: o.value,
            }))}
            onChange={(value) => onCategoryVisibilityChange(value ?? '')}
            className={style.filterSelect}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={onReloadFilters}
            loading={isFetching}
            aria-label="Refresh"
          />
        </div>
        <div className={style.actions}>
          <Tooltip title={total >= maxCategories ? `Maximum ${MAX_FAQ_CATEGORIES} categories` : ''}>
            <ProtectedButton
              icon={<PlusOutlined />}
              onClick={onCreateCategory}
              disabled={total >= maxCategories}
              feature={FEATURE.FAQ_CREATE}
              requireCRUD
            >
              Create Category
            </ProtectedButton>
          </Tooltip>
          <Tooltip title={allCategoriesLoading ? '' : createArticleBlockedReason}>
            <ProtectedButton
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(PAGES.FAQ_ARTICLE_CREATE)}
              disabled={allCategoriesLoading || !!createArticleBlockedReason}
              feature={FEATURE.FAQ_CREATE}
              requireCRUD
            >
              Create Article
            </ProtectedButton>
          </Tooltip>
        </div>
      </div>

      {canEdit && filtersActive ? (
        <p className={style.reorderNote}>Clear search and filters to reorder.</p>
      ) : null}

      {isLoading ? (
        <div className={style.loadingWrap}>
          <Spin />
        </div>
      ) : items.length === 0 ? (
        <div className={style.emptyState}>
          <InboxOutlined />
          <p className={style.emptyTitle}>{emptyCopy.title}</p>
          {emptyCopy.description ? (
            <p className={style.emptyDescription}>{emptyCopy.description}</p>
          ) : null}
          {total === 0 ? (
            <ProtectedButton
              type="primary"
              icon={<PlusOutlined />}
              onClick={onCreateCategory}
              feature={FEATURE.FAQ_CREATE}
              requireCRUD
            >
              Create Category
            </ProtectedButton>
          ) : null}
        </div>
      ) : (
        <div className={style.list}>
          {items.map((category, index) => {
            const expanded = expandedIds.has(category._id)
            const articles = category.articles ?? []
            return (
              <div key={category._id} className={style.categoryBlock}>
                <div
                  className={`${style.categoryRow} ${
                    dragOverCategoryIndex === index
                      ? draggedArticle
                        ? style.categoryRowArticleDrop
                        : style.dragOver
                      : ''
                  }`}
                  onDragOver={(e) => {
                    if (dragDisabled) {
                      return
                    }
                    if (!draggedArticle && !draggedCategoryId) {
                      return
                    }
                    e.preventDefault()
                    setDragOverCategoryIndex(index)
                  }}
                  onDrop={(e) => {
                    if (dragDisabled) {
                      return
                    }
                    e.preventDefault()
                    if (draggedArticle) {
                      handleArticleDrop(category, articles.length, articles)
                      return
                    }
                    if (draggedCategoryId) {
                      handleCategoryDrop(index)
                    }
                  }}
                >
                  {dragDisabled ? (
                    <span className={style.dragSpacer} aria-hidden />
                  ) : (
                    <Tooltip title="Drag to reorder">
                      <span
                        className={style.dragHandle}
                        draggable
                        onDragStart={() => {
                          setDraggedCategoryId(category._id)
                          setDraggedArticle(null)
                        }}
                        onDragEnd={clearDragState}
                      >
                        <HolderOutlined />
                      </span>
                    </Tooltip>
                  )}
                  <button
                    type="button"
                    className={style.expandBtn}
                    onClick={() => toggleExpanded(category._id)}
                    aria-expanded={expanded}
                    aria-label={expanded ? 'Collapse category' : 'Expand category'}
                  >
                    <RightOutlined className={expanded ? style.chevronExpanded : undefined} />
                  </button>
                  <button
                    type="button"
                    className={style.nameBtn}
                    onClick={() => toggleExpanded(category._id)}
                  >
                    <Tooltip title={category.name}>
                      <span className={style.categoryName}>{category.name}</span>
                    </Tooltip>
                    <span className={style.articleCount}>
                      ({category.articleCount}/{MAX_FAQ_ARTICLES_PER_CATEGORY})
                    </span>
                    <span
                      className={
                        category.visibility === 'VISIBLE'
                          ? style.articleStatusPublished
                          : style.articleStatusUnpublished
                      }
                    >
                      {category.visibility === 'VISIBLE' ? 'Visible' : 'Hidden'}
                    </span>
                  </button>
                  {canEdit ? (
                    <div className={style.rowActions}>
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => onEditCategory(category)}
                        aria-label="Edit category"
                      />
                      <Dropdown menu={{ items: moreMenu(category) }} trigger={['click']}>
                        <Button type="text" icon={<MoreOutlined />} aria-label="More actions" />
                      </Dropdown>
                    </div>
                  ) : null}
                </div>
                {expanded ? (
                  <div
                    className={style.articlesPanel}
                    onDragOver={(e) => {
                      if (dragDisabled || !draggedArticle) {
                        return
                      }
                      e.preventDefault()
                    }}
                    onDrop={(e) => {
                      if (dragDisabled || !draggedArticle) {
                        return
                      }
                      e.preventDefault()
                      handleArticleDrop(category, articles.length, articles)
                    }}
                  >
                    {articles.length === 0 ? (
                      <p className={style.noArticles}>No articles yet</p>
                    ) : (
                      <ul className={style.articleList}>
                        {articles.map((article, articleIndex) => {
                          const rowKey = `${category._id}:${article._id}`
                          return (
                            <li
                              key={article._id}
                              className={`${style.articleRow} ${dragOverArticleKey === rowKey ? style.articleDragOver : ''}`}
                              onDragOver={(e) => {
                                if (dragDisabled || !draggedArticle) {
                                  return
                                }
                                e.preventDefault()
                                e.stopPropagation()
                                setDragOverArticleKey(rowKey)
                              }}
                              onDrop={(e) => {
                                if (dragDisabled || !draggedArticle) {
                                  return
                                }
                                e.preventDefault()
                                e.stopPropagation()
                                handleArticleDrop(category, articleIndex, articles)
                              }}
                            >
                              {dragDisabled ? (
                                // no handle while filtering or for view-only roles; keep its grid column
                                <span aria-hidden />
                              ) : (
                                <Tooltip title="Drag to reorder">
                                  <span
                                    className={style.articleDragHandle}
                                    draggable
                                    onDragStart={(e) => {
                                      e.stopPropagation()
                                      setDraggedArticle({
                                        id: article._id,
                                        sourceCategoryId: category._id,
                                      })
                                      setDraggedCategoryId(null)
                                    }}
                                    onDragEnd={clearDragState}
                                  >
                                    <HolderOutlined />
                                  </span>
                                </Tooltip>
                              )}
                              <Tooltip title={article.title}>
                                <Link
                                  to={generatePath(PAGES.FAQ_ARTICLE_DETAIL, { id: article._id })}
                                  className={style.articleTitle}
                                  draggable={false}
                                >
                                  {article.title}
                                </Link>
                              </Tooltip>
                              <span
                                className={
                                  article.status === 'PUBLISHED'
                                    ? style.articleStatusPublished
                                    : style.articleStatusUnpublished
                                }
                              >
                                <span className={style.statusDot} aria-hidden />
                                {article.status === 'PUBLISHED' ? 'Published' : 'Unpublished'}
                              </span>
                              <span className={style.articleUpdated}>
                                {formatDateWithPattern(article.updatedAt, 'DD/MM/YYYY HH:mm')}
                              </span>
                              {canEdit ? (
                                <Dropdown
                                  menu={{ items: articleMenu(category, article, articleIndex) }}
                                  trigger={['click']}
                                >
                                  <Button
                                    type="text"
                                    icon={<MoreOutlined />}
                                    aria-label="Article actions"
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                </Dropdown>
                              ) : (
                                // keep the actions grid column
                                <span aria-hidden />
                              )}
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      )}

      <Modal
        open={!!pendingMove}
        title="Move article"
        onCancel={() => setPendingMove(null)}
        maskClosable={false}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => setPendingMove(null)}>
            Cancel
          </Button>,
          <Button
            key="move"
            type="primary"
            loading={reorderArticle.isPending}
            onClick={() => void confirmPendingMove()}
          >
            Move
          </Button>,
        ]}
      >
        {pendingMove ? (
          <>Move this article to {pendingMove.targetCategoryName}?</>
        ) : null}
      </Modal>

      <Modal
        open={!!cannotDeleteTarget}
        title="Cannot delete category"
        onCancel={() => setCannotDeleteTarget(null)}
        destroyOnClose
        footer={[
          <Button key="ok" type="primary" onClick={() => setCannotDeleteTarget(null)}>
            OK
          </Button>,
        ]}
      >
        {cannotDeleteTarget ? (
          <>
            This category has <strong>{cannotDeleteTarget.articleCount}</strong> article(s). Remove
            or move articles before deleting.
          </>
        ) : null}
      </Modal>

      <Modal
        open={!!visibilityTarget}
        title={
          visibilityTarget?.visibility === 'VISIBLE' ? 'Hide category' : 'Show category'
        }
        onCancel={() => setVisibilityTarget(null)}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => setVisibilityTarget(null)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={updateCategory.isPending}
            onClick={() => void handleVisibilityChange()}
          >
            {visibilityTarget?.visibility === 'VISIBLE' ? 'Hide' : 'Show'}
          </Button>,
        ]}
      >
        {visibilityTarget?.visibility === 'VISIBLE' ? (
          <>
            Hide <strong>{visibilityTarget.name}</strong>? The category and its articles will not
            appear on user web. Article publish status will not change.
          </>
        ) : visibilityTarget ? (
          <>
            Show <strong>{visibilityTarget.name}</strong>? Published articles will appear on user
            web when the category is visible.
          </>
        ) : null}
      </Modal>

      <Modal
        open={!!statusTarget}
        title={statusTarget?.status === 'PUBLISHED' ? 'Unpublish article' : 'Publish article'}
        onCancel={() => setStatusTarget(null)}
        maskClosable={false}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => setStatusTarget(null)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            loading={updateArticleStatus.isPending}
            onClick={() => void handleToggleArticleStatus()}
          >
            {statusTarget?.status === 'PUBLISHED' ? 'Unpublish' : 'Publish'}
          </Button>,
        ]}
      >
        {statusTarget?.status === 'PUBLISHED' ? (
          <>
            Unpublish <strong>{statusTarget.title}</strong>? The article will no longer appear on
            user web.
          </>
        ) : statusTarget ? (
          <>
            Publish <strong>{statusTarget.title}</strong>? The article will appear on user web when
            its category is visible.
          </>
        ) : null}
      </Modal>

      <Modal
        open={!!deleteArticleTarget}
        title="Delete article"
        onCancel={() => setDeleteArticleTarget(null)}
        maskClosable={false}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => setDeleteArticleTarget(null)}>
            Cancel
          </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            loading={deleteArticle.isPending}
            onClick={() => deleteArticleTarget && void handleDeleteArticle(deleteArticleTarget._id)}
          >
            Delete
          </Button>,
        ]}
      >
        {deleteArticleTarget ? (
          <>
            Are you sure you want to delete <strong>{deleteArticleTarget.title}</strong>? This action
            cannot be undone.
          </>
        ) : null}
      </Modal>

      <Modal
        open={!!deleteTarget}
        title="Delete category"
        onCancel={() => setDeleteTarget(null)}
        destroyOnClose
        footer={[
          <Button key="cancel" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>,
          <Button
            key="ok"
            type="primary"
            danger
            loading={deleteCategory.isPending}
            onClick={() => deleteTarget && handleDelete(deleteTarget._id)}
          >
            Delete
          </Button>,
        ]}
      >
        Are you sure you want to delete this category? This action cannot be undone.
      </Modal>
    </div>
  )
}
